import { create } from 'zustand'
import type {
  AppState, FactorySnapshot, InstanceConfig, InstanceSummary,
  KpisSSE, MachineStateSSE, WorkerStateSSE, ActiveJobSSE,
  Notification, RunHistory,
} from '../types'

interface InspectorTarget {
  kind: 'machine' | 'job'
  id: string
}

interface SimStore {
  // ── App state machine ─────────────────────────────────────────────────────
  appState: AppState
  setAppState: (s: AppState) => void

  // ── Instance selection ────────────────────────────────────────────────────
  instances: InstanceSummary[]
  setInstances: (list: InstanceSummary[]) => void
  instanceConfig: InstanceConfig | null
  setInstanceConfig: (cfg: InstanceConfig | null) => void
  selectedInstance: string
  setSelectedInstance: (name: string) => void
  selectedDispatcher: string
  setSelectedDispatcher: (d: string) => void
  rtFactor: number
  setRtFactor: (f: number) => void

  // ── Active run ────────────────────────────────────────────────────────────
  runId: string | null
  setRunId: (id: string | null) => void
  simTime: number
  setSimTime: (t: number) => void
  resetRun: () => void

  // ── Phase 31: live factory snapshot (from REST polling) ──────────────────
  snapshot: FactorySnapshot | null
  setSnapshot: (s: FactorySnapshot | null) => void

  // ── Live sim state (from SSE) ─────────────────────────────────────────────
  machines: Record<string, MachineStateSSE>
  setMachines: (m: Record<string, MachineStateSSE>) => void
  workers: WorkerStateSSE[]
  setWorkers: (w: WorkerStateSSE[]) => void
  activeJobs: ActiveJobSSE[]
  setActiveJobs: (j: ActiveJobSSE[]) => void
  kpis: KpisSSE | null
  setKpis: (k: KpisSSE) => void
  finalKpis: Record<string, number> | null
  setFinalKpis: (k: Record<string, number>) => void
  completedOrders: Map<string, { sim_time: number; is_late: boolean; actual_start: number }>
  markOrderComplete: (id: string, sim_time: number, is_late: boolean, actual_start?: number) => void
  clearCompleted: () => void
  // Tracks actual_start for every order that has ever touched a machine,
  // persisting across routing steps so the Gantt fill never disappears mid-transit.
  startedOrders: Map<string, number>
  markOrderStarted: (id: string, actual_start: number) => void

  // ── UI ────────────────────────────────────────────────────────────────────
  notifications: Notification[]
  addNotification: (msg: string, level?: Notification['level']) => void
  dismissNotification: (id: string) => void

  inspector: InspectorTarget | null
  setInspector: (t: InspectorTarget | null) => void

  // ── History ───────────────────────────────────────────────────────────────
  runHistory: RunHistory[]
  setRunHistory: (h: RunHistory[]) => void
}

let _notifSeq = 0

export const useSimStore = create<SimStore>((set) => ({
  appState: 'LOBBY',
  setAppState: (s) => set({ appState: s }),

  instances: [],
  setInstances: (list) => set({ instances: list }),
  instanceConfig: null,
  setInstanceConfig: (cfg) => set({ instanceConfig: cfg }),
  selectedInstance: '',
  setSelectedInstance: (name) => set({ selectedInstance: name }),
  selectedDispatcher: 'fifo',
  setSelectedDispatcher: (d) => set({ selectedDispatcher: d }),
  rtFactor: 0.05,
  setRtFactor: (f) => set({ rtFactor: f }),

  runId: null,
  setRunId: (id) => set({ runId: id }),
  simTime: 0,
  setSimTime: (t) => set({ simTime: t }),
  resetRun: () => set({
    runId: null,
    simTime: 0,
    snapshot: null,
    machines: {},
    activeJobs: [],
    kpis: null,
    finalKpis: null,
    completedOrders: new Map(),
    startedOrders: new Map(),
    inspector: null,
  }),

  snapshot: null,
  setSnapshot: (s) => set({ snapshot: s }),

  machines: {},
  setMachines: (m) => set({ machines: m }),
  workers: [],
  setWorkers: (w) => set({ workers: w }),
  activeJobs: [],
  setActiveJobs: (j) => set({ activeJobs: j }),
  kpis: null,
  setKpis: (k) => set({ kpis: k }),
  finalKpis: null,
  setFinalKpis: (k) => set({ finalKpis: k }),
  completedOrders: new Map(),
  markOrderComplete: (id, sim_time, is_late, actual_start = 0) =>
    set((s) => {
      const next = new Map(s.completedOrders)
      next.set(id, { sim_time, is_late, actual_start })
      return { completedOrders: next }
    }),
  clearCompleted: () => set({ completedOrders: new Map() }),
  startedOrders: new Map(),
  markOrderStarted: (id, actual_start) =>
    set((s) => {
      if (s.startedOrders.has(id)) return {}  // already recorded
      const next = new Map(s.startedOrders)
      next.set(id, actual_start)
      return { startedOrders: next }
    }),

  notifications: [],
  addNotification: (msg, level = 'info') =>
    set((s) => {
      const note: Notification = { id: String(++_notifSeq), message: msg, level, timestamp: Date.now() }
      return { notifications: [note, ...s.notifications].slice(0, 5) }
    }),
  dismissNotification: (id) =>
    set((s) => ({ notifications: s.notifications.filter((n) => n.id !== id) })),

  inspector: null,
  setInspector: (t) => set({ inspector: t }),

  runHistory: [],
  setRunHistory: (h) => set({ runHistory: h }),
}))
